import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Chip, Link as MuiLink, Stack, Tooltip, Typography } from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';

import type { MarketingRouterRuleRecord } from '~/hooks/apis/promo/useMarketingRouterRules';

import MarketingRouterTimelineTooltip, { ruleDetailPath } from './MarketingRouterTimelineTooltip';
import { MARKETING_ROUTER_STATUS_STYLE, getMarketingRouterRuleStatus } from './marketingRouterRuleFilters';
import { type TimelineWindow, getBarPlacement } from './marketingRouterTimelineLayout';

const LABEL_WIDTH = 240;
const ROW_HEIGHT = 44;
/** Keep columns readable when a month is shown on a narrow screen. */
const MIN_TRACK_WIDTH = 640;

type Props = {
  rules: Array<MarketingRouterRuleRecord>;
  today: string;
  window: TimelineWindow;
};

type ColumnBg = string | ((theme: Theme) => string) | undefined;

/**
 * Background for a day column: today gets a distinct primary (blue) tint so it
 * stands apart from the grey weekend tint; weekends are tinted next.
 */
function columnBg(tick: { day: string; isWeekend: boolean }, today: string): ColumnBg {
  if (tick.day === today) {
    return (theme) => alpha(theme.palette.primary.main, 0.28);
  }
  return tick.isWeekend ? 'action.hover' : undefined;
}

/**
 * Gantt-style view of the marketing router rules already filtered by the page.
 * Positions come from `marketingRouterTimelineLayout` (pure, unit-tested); this file
 * only lays them out with MUI + theme tokens.
 */
export default function MarketingRouterTimeline({ rules, today, window }: Props) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: LABEL_WIDTH + MIN_TRACK_WIDTH }}>
        {/* Date axis */}
        <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ width: LABEL_WIDTH, flexShrink: 0, p: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Rule
            </Typography>
          </Box>
          <Box sx={{ flex: 1, display: 'flex' }}>
            {window.ticks.map((tick) => (
              <Box
                key={tick.day}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                  borderLeft: 1,
                  borderColor: 'divider',
                  bgcolor: columnBg(tick, today),
                }}
              >
                <Typography
                  variant="caption"
                  color={tick.day === today ? 'primary.main' : 'text.secondary'}
                  fontWeight={tick.day === today ? 600 : undefined}
                  noWrap
                >
                  {tick.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Stack>

        {/* Rule rows */}
        {rules.map((rule) => {
          const status = getMarketingRouterRuleStatus(rule, today);
          const placement = getBarPlacement(rule, window);

          return (
            <Stack
              key={rule.id}
              direction="row"
              sx={{ minHeight: ROW_HEIGHT, borderBottom: 1, borderColor: 'divider' }}
            >
              <Box
                sx={{
                  width: LABEL_WIDTH,
                  flexShrink: 0,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 0.25,
                }}
              >
                <MuiLink component={Link} to={ruleDetailPath(rule.id)} underline="hover" variant="body2" noWrap>
                  {rule.name}
                </MuiLink>
                <Chip
                  label={status}
                  size="small"
                  color={MARKETING_ROUTER_STATUS_STYLE[status].chipColor}
                  sx={{ alignSelf: 'flex-start' }}
                />
              </Box>

              {/* The track stretches to the row height so grid lines and the
                  weekend/today tint reach the bottom border. */}
              <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                {/* Grid lines + weekend/today tint behind the bar */}
                {window.ticks.map((tick) => (
                  <Box
                    key={tick.day}
                    sx={{
                      flex: 1,
                      borderLeft: 1,
                      borderColor: 'divider',
                      bgcolor: columnBg(tick, today),
                    }}
                  />
                ))}

                {placement && (
                  <Tooltip
                    title={<MarketingRouterTimelineTooltip rule={rule} status={status} />}
                    arrow
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          border: 1,
                          borderColor: 'divider',
                          boxShadow: 3,
                        },
                      },
                      arrow: {
                        sx: {
                          color: 'background.paper',
                          '&::before': { border: 1, borderColor: 'divider' },
                        },
                      },
                    }}
                  >
                    <Box
                      component={Link}
                      to={ruleDetailPath(rule.id)}
                      aria-label={`Open rule ${rule.name}`}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        left: `${placement.leftPct}%`,
                        width: `${placement.widthPct}%`,
                        minWidth: 6,
                        height: 20,
                        borderRadius: 1,
                        bgcolor: MARKETING_ROUTER_STATUS_STYLE[status].palette,
                        borderTopLeftRadius: placement.clippedStart ? 0 : undefined,
                        borderBottomLeftRadius: placement.clippedStart ? 0 : undefined,
                        borderTopRightRadius: placement.clippedEnd ? 0 : undefined,
                        borderBottomRightRadius: placement.clippedEnd ? 0 : undefined,
                        cursor: 'pointer',
                        display: 'block',
                        '&:hover': { filter: 'brightness(0.92)' },
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}
